/**
 * Announcing that the learner changed something.
 *
 * Only *learner* writes are announced. A pull applying what the server already
 * holds must not be, or the app queues an operation to send the server its own
 * record back — a round trip for nothing, a revision bumped for nothing, and a
 * conflict manufactured out of thin air when a second device pulls the same
 * record at the same moment.
 *
 * The origin is **which storage object you hold**, not when you write. An
 * earlier version used an ambient "currently applying a remote page" flag, and
 * it was wrong in a way worth remembering: the flag is global and spans awaits,
 * so a learner finishing a lesson task while a pull happened to be in flight had
 * that save silently dropped from the outbox. It was intermittent, it only
 * appeared under load, and it lost real work.
 *
 * So the engine reads and writes through raw storage that never announces, and
 * feature screens use the announcing storage. There is no window during which
 * the wrong thing happens, because there is no window.
 */

/** The kinds the sync contract carries. Matches `SyncKind` in `src/sync/types.ts`. */
export type WriteKind = 'document' | 'lesson_progress' | 'user_settings' | 'practice_session';

export interface RecordWrite {
  kind: WriteKind;
  id: string;
  deleted?: boolean;
}

type WriteListener = (write: RecordWrite) => void;

/**
 * A set, not a single slot.
 *
 * With one slot, a second subscriber silently evicts the first — and during a
 * React remount the new subscription is registered before the old one's cleanup
 * runs, so the surviving listener is the *dead* one. That is a lost write, and
 * it is invisible.
 */
const listeners = new Set<WriteListener>();

/** Register a listener. Returns an unsubscribe. */
export function observeWrites(next: WriteListener | null): () => void {
  if (!next) return () => {};
  listeners.add(next);
  return () => {
    listeners.delete(next);
  };
}

/**
 * Tell the listener the learner changed a record.
 *
 * A listener must never be able to break a local save: sync is the enhancement,
 * and the write on this iPad is the product.
 */
export function announceWrite(write: RecordWrite): void {
  for (const listener of [...listeners]) {
    try {
      listener(write);
    } catch {
      /* see above */
    }
  }
}

/** Test seam: forget every listener. */
export function resetWriteLog(): void {
  listeners.clear();
}

/** Test seam: how many listeners are registered. */
export function listenerCount(): number {
  return listeners.size;
}
